import {
  CreateParams,
  CreateResult,
  DataProvider as RaDataProvider,
  DeleteManyResult,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  RaRecord,
  UpdateManyResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";

import { getJson, postJson, putJson, deleteJson } from "../../utils/api";
import UserHandler from "./handlers/UserHandler";
import HistoryHandler from "./handlers/HistoryHandler";
import ThreadsHandler from "./handlers/ThreadsHandler";
import LogsHandler from "./handlers/LogsHandler";
import CurriculumHandler from "./handlers/CurriculumHandler";
import LearningUsersHandler from "./handlers/LearningUsersHandler";

const resourceHandlerMap = new Map<string, any>([
  ["users", UserHandler],
  ["history", HistoryHandler],
  ["threads", ThreadsHandler],
  ["logs", LogsHandler],
  ["curricula", CurriculumHandler],
  ["learning-users", LearningUsersHandler],
]);

const getResourceName = (resource: string): string => {
  const resourceComponents = resource.split("/");
  return resourceComponents.length > 0
    ? resourceComponents[resourceComponents.length - 1]
    : resource;
};

class DataProvider implements RaDataProvider {
  async getList<RecordType extends RaRecord = any>(
    resource: string,
    params: GetListParams
  ): Promise<GetListResult<RecordType>> {
    const resourceName = getResourceName(resource);
    const handler = resourceHandlerMap.get(resourceName);
    if (handler?.getListHandler) {
      return handler.getListHandler(resource, params);
    }

    return getJson(`/${resource}`).then(async (response) => {
      let { data: responseData } = await response.json();

      responseData = responseData.Items ?? responseData;
      responseData = responseData?.length > 0 ? responseData : [];

      return {
        data: responseData,
        total: responseData.length,
      };
    });
  }

  async getOne<RecordType extends RaRecord = any>(
    resource: string,
    params: GetOneParams<any>
  ): Promise<GetOneResult<RecordType>> {
    const { id } = params;
    params.id = id === "undefined" ? "" : id;

    const resourceName = getResourceName(resource);
    const handler = resourceHandlerMap.get(resourceName);
    if (handler?.getOneHandler) {
      return handler.getOneHandler(resource, params);
    }

    return getJson(`/${resource}/${id}`).then(async (response) => {
      const { data } = await response.json();
      return {
        data,
      };
    });
  }

  async getMany<RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyParams
  ): Promise<GetManyResult<RecordType>> {
    const resourceName = getResourceName(resource);
    const handler = resourceHandlerMap.get(resourceName);
    if (handler?.getMany) {
      return handler.getMany(resource, params);
    }

    throw new Error("Method not available");
  }

  async getManyReference<RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyReferenceParams
  ): Promise<GetManyReferenceResult<RecordType>> {
    const resourceName = getResourceName(resource);
    const handler = resourceHandlerMap.get(resourceName);
    if (handler?.getManyReference) {
      return handler.getManyReference(resource, params);
    }

    return Promise.reject(new Error("Method not available"));
  }

  async update<RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateParams<any>
  ): Promise<UpdateResult<RecordType>> {
    const { id } = params;
    params.id = id === "undefined" ? "" : id;

    const resourceName = getResourceName(resource);
    const handler = resourceHandlerMap.get(resourceName);
    if (handler?.updateHandler) {
      return handler.updateHandler(resource, params);
    }
    const payload = params.data;

    return putJson(`/${resource}/${id}`, payload).then(async (response) => {
      const { data } = await response.json();

      return {
        data,
      };
    });
  }

  // this should update an array of ids, the api doesn't have this feature
  async updateMany<RecordType extends RaRecord = any>(): Promise<
    UpdateManyResult<RecordType>
  > {
    return Promise.reject(new Error("Method not available"));
  }

  async create<RecordType extends RaRecord = any>(
    resource: string,
    params: CreateParams<any>
  ): Promise<CreateResult<RecordType>> {
    const payload = params.data;

    const resourceName = getResourceName(resource);
    const handler = resourceHandlerMap.get(resourceName);
    if (handler?.createHandler) {
      return handler.createHandler(resource, params);
    }

    return postJson(`/${resource}`, payload).then(async (response) => {
      const { data } = await response.json();

      return {
        data,
      };
    });
  }

  async delete<RecordType extends RaRecord = any>(
    resource: string,
    params: any
  ): Promise<DeleteResult<RecordType>> {
    const resourceName = getResourceName(resource);
    const handler = resourceHandlerMap.get(resourceName);
    if (handler?.deleteHandler) {
      return handler.deleteHandler(resource, params);
    }

    return deleteJson(`/${resource}/${params.id}`).then(async (response) => {
      const { data } = await response.json();
      return { data };
    });
  }

  // this should delete an array of ids, the api doesn't have this feature
  async deleteMany<RecordType extends RaRecord = any>(): Promise<
    DeleteManyResult<RecordType>
  > {
    return Promise.reject(new Error("Method not available"));
  }
}

export default new DataProvider();
